// rooms.helpers
export const uid = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

export const LETTERS = "АБВГДЕЖЗИКЛМНОПРСТУФХЦЧШЩЭЮЯ";

export const wallCode = (roomIndex, wallIndex) => {
    const letter = LETTERS[wallIndex] || String(wallIndex + 1);
    return `${roomIndex + 1}${letter}`;
};

export const num = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
};

export const setAt = (arr, idx, value) => arr.map((x, i) => (i === idx ? value : x));
export const removeAt = (arr, idx) => arr.filter((_, i) => i !== idx);

export const moveAt = (arr, from, to) => {
    const next = arr.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

export const openingLinkId = (o) => String(o?.doorGroupId || o?.linkId || "");
export const openingPhysicalId = (o) => {
    const link = openingLinkId(o);
    const id = String(o?.id || "");
    return link || id;
};

export const shortId = (s, n = 6) => {
    const x = String(s || "");
    if (!x) return "";
    return x.length <= n ? x : x.slice(0, n);
};

export const normalizeWall = (w, ri, wi) => ({
    id: w?.id || uid(),
    code: wallCode(ri, wi),
    name: w?.name || "",
    lengthMm: w?.lengthMm ?? "",
    heightMmOverride: w?.heightMmOverride ?? "",
    doors: Array.isArray(w?.doors) ? w.doors : [],
    windows: Array.isArray(w?.windows) ? w.windows : [],
    sockets: Array.isArray(w?.sockets) ? w.sockets : [],
    switches: Array.isArray(w?.switches) ? w.switches : [],
    notes: w?.notes || "",
});

export const normalizeCeilingLight = (x) => ({
    id: x?.id || uid(),
    type: String(x?.type || "chandelier"),
    label: String(x?.label || ""),
    qty: Math.max(0, num(x?.qty ?? 1)),
});

export const normalizeRoom = (r, ri) => {
    const shape = r?.shape === "polygon" ? "polygon" : "rectangle";
    const heightMm = num(r?.heightMm ?? 2700);

    const rect = r?.rect || {};
    const L = rect?.lengthMm ?? "";
    const W = rect?.widthMm ?? "";

    let walls = Array.isArray(r?.walls) ? r.walls : [];

    if (shape === "rectangle") {
        const need = 4;
        walls = walls.slice(0, need);
        while (walls.length < need) walls.push({});
        walls = walls.map((w, wi) => {
            const isL = wi === 0 || wi === 2;
            const isW = wi === 1 || wi === 3;
            const nextLen = w?.lengthMm ?? "";
            const autoLen = isL ? num(L) : isW ? num(W) : num(nextLen);
            return normalizeWall({ ...w, lengthMm: nextLen === "" || nextLen == null ? autoLen : nextLen }, ri, wi);
        });
    } else {
        walls = walls.map((w, wi) => normalizeWall(w, ri, wi));
    }

    const ceilingLights = (Array.isArray(r?.ceilingLights) ? r.ceilingLights : []).map(normalizeCeilingLight);

    return {
        id: r?.id || uid(),
        name: r?.name || `Комната ${ri + 1}`,
        shape,
        heightMm,
        floor: r?.floor || "lam",
        warm: !!r?.warm,
        rect: { lengthMm: L, widthMm: W },
        floorAreaM2Override: r?.floorAreaM2Override ?? "",
        ceilingAreaM2Override: r?.ceilingAreaM2Override ?? "",
        ceilingType: String(r?.ceilingType || "stretch"),
        ceilingDropMm: r?.ceilingDropMm ?? "",
        ceilingLights,
        walls,
        notes: r?.notes || "",
        len: r?.len ?? "",
        wid: r?.wid ?? "",
        area: r?.area ?? 0,
        wall: r?.wall ?? 0,
    };
};

export const normalizeRooms = (rooms) => (Array.isArray(rooms) ? rooms : []).map((r, ri) => normalizeRoom(r, ri));

export const makeOpening = () => ({
    id: uid(),
    linkId: "",
    doorGroupId: "",
    label: "",
    qty: 1,
    widthMm: "",
    heightMm: "",
});

export const makePoint = () => ({ id: uid(), label: "", qty: 1, cableSectionMm2: "2.5", notes: "" });

export const makeCeilingLight = () => ({
    id: uid(),
    type: "chandelier",
    label: "",
    qty: 1,
});

export const fmt2 = (v) => {
    const x = Number(v);
    if (!Number.isFinite(x)) return "0.00";
    return x.toFixed(2);
};

export const mapToStr = (m) => {
    const keys = Object.keys(m || {});
    if (!keys.length) return "-";
    return keys
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => `${k}: ${m[k]}`)
        .join(", ");
};

export const buildDoorCatalog = (rooms) => {
    const R = Array.isArray(rooms) ? rooms : [];
    const map = {};

    R.forEach((room, ri) => {
        const walls = Array.isArray(room?.walls) ? room.walls : [];
        walls.forEach((wall, wi) => {
            const wCode = String(wall?.code || wallCode(ri, wi));
            const doors = Array.isArray(wall?.doors) ? wall.doors : [];
            doors.forEach((d, oi) => {
                const pid = openingPhysicalId(d);
                if (!pid) return;

                if (!map[pid]) {
                    map[pid] = {
                        id: pid,
                        label: String(d?.label || ""),
                        widthMm: num(d?.widthMm),
                        heightMm: num(d?.heightMm),
                        qty: Math.max(0, num(d?.qty ?? 1)),
                        refs: [],
                    };
                } else {
                    if (!map[pid].label && d?.label) map[pid].label = String(d.label);
                    if (!map[pid].widthMm && num(d?.widthMm)) map[pid].widthMm = num(d.widthMm);
                    if (!map[pid].heightMm && num(d?.heightMm)) map[pid].heightMm = num(d.heightMm);
                }

                map[pid].refs.push({
                    ri,
                    wi,
                    oi,
                    wallCode: wCode,
                    roomName: room?.name || `Комната ${ri + 1}`,
                });
            });
        });
    });

    return Object.values(map)
        .map((e) => {
            const codes = Array.from(new Set((e.refs || []).map((r) => r.wallCode).filter(Boolean)));
            const size = e.widthMm && e.heightMm ? `${e.widthMm}×${e.heightMm}` : "";
            const parts = [];
            if (codes.length) parts.push(`[${codes.join(" ↔ ")}]`);
            parts.push(e.label ? e.label : "Дверь");
            if (size) parts.push(size);
            return { ...e, codes, title: parts.join(" ") };
        })
        .sort((a, b) => (a.title || "").localeCompare(b.title || "", "ru"));
};
